// @spec INC-STR-051
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Timer } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SLAConfigCard } from '@/features/incidents/admin/sla-config';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/incidents/settings/sla')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.slaConfiguration'),
  }),
  component: SLAConfigPage,
});

/**
 * SLA Configuration page for managing incident SLA thresholds.
 *
 * Access:
 * - All authenticated users can view
 * - Only org:admin roles can edit
 */
function SLAConfigPage() {
  const { isAdmin } = useOrgRole();

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Header */}
        <header className="space-y-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                window.history.back();
              }}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <Timer className="h-6 w-6 text-muted-foreground" />
                <h1 className="text-3xl font-semibold text-foreground">SLA Configuration</h1>
              </div>
              <p className="mt-2 text-muted-foreground">
                Configure Service Level Agreement thresholds for incident response times, resolution
                times, and escalation triggers by severity level.
              </p>
            </div>
          </div>
        </header>

        {/* Main content */}
        <SLAConfigCard isAdmin={isAdmin} />

        {/* NIS2 guidance */}
        <section className="mt-8 border-t pt-8">
          <h2 className="mb-4 text-lg font-semibold">NIS2 Compliance Guidance</h2>
          <div className="grid grid-cols-1 gap-6 text-sm md:grid-cols-3">
            <div>
              <h3 className="mb-2 font-medium">Incident Notification</h3>
              <p className="text-muted-foreground">
                NIS2 requires significant incidents to be reported to authorities within 24 hours of
                detection. Configure your Critical response time accordingly.
              </p>
            </div>
            <div>
              <h3 className="mb-2 font-medium">Business Hours</h3>
              <p className="text-muted-foreground">
                For regulatory incidents, consider using calendar hours instead of business hours to
                ensure compliance with 24-hour notification requirements.
              </p>
            </div>
            <div>
              <h3 className="mb-2 font-medium">Escalation</h3>
              <p className="text-muted-foreground">
                Set escalation thresholds to ensure incidents are elevated to management before
                breaching SLA or regulatory deadlines.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
